import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import type { ChatResponse, Slot, Surface } from '@ampshift/shared';
import type { ApiClient } from '../api';
import { useI18n, toLanguageCode, formatDateTime } from '../i18n';
import { SurfaceView, cardFromPendingAction, type ConfirmationCard } from './Surfaces';

interface Message { id: number; role: 'user' | 'assistant'; text: string; surfaces: Surface[] }

export interface ChatHandle { send: (text: string) => Promise<void> }

interface ChatProps { api: ApiClient; onDataChanged?: () => void }

/** Conversation-first entry point: POST /v1/chat, render the returned surfaces, confirm via /v1/chat/confirm. */
export const Chat = forwardRef<ChatHandle, ChatProps>(function Chat({ api, onDataChanged }, ref) {
  const { t, lang } = useI18n();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>(undefined);
  const logRef = useRef<HTMLDivElement>(null);
  const seq = useRef(0);

  const push = (m: Omit<Message, 'id'>) => setMessages((prev) => [...prev, { ...m, id: ++seq.current }]);

  const applyResponse = (res: ChatResponse) => {
    setConversationId(res.conversationId);
    push({ role: 'assistant', text: res.text, surfaces: res.surfaces });
    if (res.trace.toolCalls.some((c) => c.ok && /^(confirm|cancel|reschedule)/.test(c.name))) onDataChanged?.();
  };

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    push({ role: 'user', text: trimmed, surfaces: [] });
    setInput('');
    setBusy(true);
    try {
      applyResponse(await api.chat({ message: trimmed, conversationId, inputMode: 'text', locale: toLanguageCode(lang), clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }));
    } catch (e) {
      push({ role: 'assistant', text: '', surfaces: [{ type: 'error', code: 'CHAT_FAILED', message: e instanceof Error ? e.message : t('error'), recoverable: true }] });
    } finally {
      setBusy(false);
    }
  };

  useImperativeHandle(ref, () => ({ send }), [send]);

  useEffect(() => {
    const el = logRef.current;
    if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight });
  }, [messages]);

  const decide = async (card: ConfirmationCard, decision: 'confirm' | 'reject') => {
    setBusy(true);
    try {
      applyResponse(await api.confirm({ pendingActionId: card.pendingActionId, decision, acknowledgedFindingIds: [], conversationId }));
      if (decision === 'confirm') onDataChanged?.();
    } catch (e) {
      push({ role: 'assistant', text: '', surfaces: [{ type: 'error', code: 'CONFIRM_FAILED', message: e instanceof Error ? e.message : t('error'), recoverable: true }] });
    } finally {
      setBusy(false);
    }
  };

  const pickSlot = async (slot: Slot) => {
    setBusy(true);
    try {
      const out = await api.tool('createAppointmentDraft', { providerId: slot.providerId, start: slot.start, appointmentTypeCode: slot.appointmentTypeCode });
      const card = cardFromPendingAction(out.pendingAction, t('bookingTitle'), [
        { label: t('provider'), value: slot.providerName ?? out.appointment.providerName ?? slot.providerId },
        { label: t('date'), value: formatDateTime(slot.start, lang) },
        { label: t('appointmentType'), value: slot.appointmentTypeCode },
      ]);
      push({ role: 'assistant', text: '', surfaces: [card] });
    } catch (e) {
      push({ role: 'assistant', text: '', surfaces: [{ type: 'error', code: 'DRAFT_FAILED', message: e instanceof Error ? e.message : t('error'), recoverable: true }] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" aria-labelledby="chat-title">
      <h2 id="chat-title">{t('chatTitle')}</h2>
      <div className="chat-log" ref={logRef} aria-live="polite">
        {messages.map((m) => (
          <div key={m.id} className={`msg msg-${m.role}`}>
            <div className="msg-role">{m.role === 'user' ? t('youSaid') : t('assistant')}</div>
            {m.text && <div className="surface-text">{m.text}</div>}
            {m.surfaces.map((s, i) => (
              <SurfaceView key={i} surface={s} handlers={{ onSlotPick: pickSlot, onConfirm: (c) => decide(c, 'confirm'), onReject: (c) => decide(c, 'reject'), onReply: (text) => void send(text) }} />
            ))}
          </div>
        ))}
      </div>
      <form className="chat-form" onSubmit={(e) => { e.preventDefault(); void send(input); }}>
        <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t('chatPlaceholder')} aria-label={t('chatTitle')} disabled={busy} />
        <button className="btn btn-primary" type="submit" disabled={busy || !input.trim()}>{busy ? t('sending') : t('send')}</button>
      </form>
    </section>
  );
});
